type TimezoneOption = {
    value: string;
    label: string;
};

const LAYOUT_CONST = {
    VERTICAL_LAYOUT: 'vertical',
    HORIZONTAL_LAYOUT: 'horizontal',
    DEFAULT_DRAWER: 'default',
    MINI_DRAWER: 'mini-drawer'
};

const formatTimezoneShortCode = (timeZone: string) => {
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone,
        timeZoneName: 'short'
    });
    const timezonePart = formatter.formatToParts(new Date()).find(({ type }) => type === 'timeZoneName')?.value;
    return timezonePart ?? 'TZ';
};

const AMERICA_TIMEZONES = [
    'America/Adak',
    'America/Anchorage',
    'America/Anguilla',
    'America/Antigua',
    'America/Araguaina',
    'America/Argentina/La_Rioja',
    'America/Argentina/Rio_Gallegos',
    'America/Argentina/Salta',
    'America/Argentina/San_Juan',
    'America/Argentina/San_Luis',
    'America/Argentina/Tucuman',
    'America/Argentina/Ushuaia',
    'America/Aruba',
    'America/Asuncion',
    'America/Bahia',
    'America/Bahia_Banderas',
    'America/Barbados',
    'America/Belem',
    'America/Belize',
    'America/Blanc-Sablon',
    'America/Boa_Vista',
    'America/Bogota',
    'America/Boise',
    'America/Buenos_Aires',
    'America/Cambridge_Bay',
    'America/Campo_Grande',
    'America/Cancun',
    'America/Caracas',
    'America/Catamarca',
    'America/Cayenne',
    'America/Cayman',
    'America/Chicago',
    'America/Chihuahua',
    'America/Ciudad_Juarez',
    'America/Coral_Harbour',
    'America/Cordoba',
    'America/Costa_Rica',
    'America/Coyhaique',
    'America/Creston',
    'America/Cuiaba',
    'America/Curacao',
    'America/Danmarkshavn',
    'America/Dawson',
    'America/Dawson_Creek',
    'America/Denver',
    'America/Detroit',
    'America/Dominica',
    'America/Edmonton',
    'America/Eirunepe',
    'America/El_Salvador',
    'America/Fort_Nelson',
    'America/Fortaleza',
    'America/Glace_Bay',
    'America/Godthab',
    'America/Goose_Bay',
    'America/Grand_Turk',
    'America/Grenada',
    'America/Guadeloupe',
    'America/Guatemala',
    'America/Guayaquil',
    'America/Guyana',
    'America/Halifax',
    'America/Havana',
    'America/Hermosillo',
    'America/Indiana/Knox',
    'America/Indiana/Marengo',
    'America/Indiana/Petersburg',
    'America/Indiana/Tell_City',
    'America/Indiana/Vevay',
    'America/Indiana/Vincennes',
    'America/Indiana/Winamac',
    'America/Indianapolis',
    'America/Inuvik',
    'America/Iqaluit',
    'America/Jamaica',
    'America/Jujuy',
    'America/Juneau',
    'America/Kentucky/Monticello',
    'America/Kralendijk',
    'America/La_Paz',
    'America/Lima',
    'America/Los_Angeles',
    'America/Louisville',
    'America/Lower_Princes',
    'America/Maceio',
    'America/Managua',
    'America/Manaus',
    'America/Marigot',
    'America/Martinique',
    'America/Matamoros',
    'America/Mazatlan',
    'America/Mendoza',
    'America/Menominee',
    'America/Merida',
    'America/Metlakatla',
    'America/Mexico_City',
    'America/Miquelon',
    'America/Moncton',
    'America/Monterrey',
    'America/Montevideo',
    'America/Montserrat',
    'America/Nassau',
    'America/New_York',
    'America/Nome',
    'America/Noronha',
    'America/North_Dakota/Beulah',
    'America/North_Dakota/Center',
    'America/North_Dakota/New_Salem',
    'America/Ojinaga',
    'America/Panama',
    'America/Paramaribo',
    'America/Phoenix',
    'America/Port-au-Prince',
    'America/Port_of_Spain',
    'America/Porto_Velho',
    'America/Puerto_Rico',
    'America/Punta_Arenas',
    'America/Rankin_Inlet',
    'America/Recife',
    'America/Regina',
    'America/Resolute',
    'America/Rio_Branco',
    'America/Santarem',
    'America/Santiago',
    'America/Santo_Domingo',
    'America/Sao_Paulo',
    'America/Scoresbysund',
    'America/Sitka',
    'America/St_Barthelemy',
    'America/St_Johns',
    'America/St_Kitts',
    'America/St_Lucia',
    'America/St_Thomas',
    'America/St_Vincent',
    'America/Swift_Current',
    'America/Tegucigalpa',
    'America/Thule',
    'America/Tijuana',
    'America/Toronto',
    'America/Tortola',
    'America/Vancouver',
    'America/Whitehorse',
    'America/Winnipeg',
    'America/Yakutat'
] as const;

const TIMEZONE_OPTIONS: TimezoneOption[] = AMERICA_TIMEZONES.map((value) => ({
    value,
    label: `${value} (${formatTimezoneShortCode(value)})`
}));

const ET_TIMEZONE = 'America/New_York';
const JUST_SHOW_BARRIERS_WEEKDAYS_ET = new Set(['Tue', 'Wed', 'Thu', 'Fri']);

const INTAKE_METHODS = [
    { id: 'SFTP (auto-pull)', name: 'SFTP (auto-pull)' },
    { id: 'HL7 feed', name: 'HL7 Feed' },
    { id: 'Manual upload', name: 'Manual Upload' }
];
const ROWS_PER_PAGE_OPTIONS = [50, 100] as const;
const DEFAULT_ROWS_PER_PAGE = 50 as const;
const TIER_COLORS = {
    green: '#6cc067',
    yellow: '#f5c518',
    orange: '#e67e22',
    red: '#d9534f',
    gray: '#9e9e9e'
};
const TIER_LEGEND = {
    colors: {
        green: { color: TIER_COLORS.green, Legend: 'Medically stable for discharge' },
        yellow: { color: TIER_COLORS.yellow, Legend: 'Active clinical work in progress' },
        orange: { color: TIER_COLORS.orange, Legend: 'Not medically ready; acute barriers' },
        red: { color: TIER_COLORS.red, Legend: '' },
        gray: { color: TIER_COLORS.gray, Legend: '' }
    },
    tiers: {
        T1: { legend: 'Simple work today; logistics secured', color: 'green' },
        T2: { legend: 'Active complex coordination in motion', color: 'yellow' },
        T3: { legend: 'Stalled; complex; escalation required', color: 'orange' },
        ['R-C']: { legend: 'Blocked clinical complexity', color: 'red' },
        ['R-P']: { legend: 'Blocked placement complexity', color: 'red' },
        ['R-C+R-P']: { legend: 'Blocked clinical and placement complexity', color: 'red' },
        ['R-P+R-C']: { legend: 'Blocked clinical and placement complexity', color: 'red' },
        GRAY: { legend: "Can't classify", color: 'gray' }
    }
} as const;

const ENABLED_WEEKEND_DISCHARGE_HOSPITALS: string[] = ['shady grove'];
/** White Oak handoff rows are AI-generated — list chips are view-only (no detail edit). */
const READONLY_WEEKEND_DISCHARGE_HOSPITALS: string[] = ['white oak'];
/** Hospitals that support Weekend Handoff only (no General Handoff type UI). */
const WEEKEND_ONLY_HANDOFF_HOSPITALS: string[] = ['white oak'];
const LLOS_FLAGGED_HOSPITALS: string[] = ['shady grove'];
/** Launch hospitals for the Meeting Transcription crawl UI. Name match is lowercase, same as other hospital flags. */
const TRANSCRIPT_CRAWL_HOSPITALS: string[] = ['white oak', 'shady grove'];
/** White Oak census PDF and preview omit MR, IMM, and PASSR fields. */
const AI_EXTRACTED_HANDOFF_DETAILS_HOSPITALS: string[] = ['white oak'];
const LLOS_SUMMARY_POINTS = [
    { title: 'Barriers to discharge', description: 'What is preventing this patient from being discharged?' },

    {
        title: 'Support needed',
        description: 'What do you need from the care team or other resources to move this case forward?'
    },
    {
        title: 'Proposed solutions',
        description: 'What would a path to discharge look like from here?'
    }
];

const SILVERSTAY_URL = 'https://www.silverstay.com';
const SILVERSTAY_URL_WITHOUT_HTTP = SILVERSTAY_URL.replace('https://', '');
const SILVERSTAY_SUPPORT =
    'For technical questions, data gaps, or patient inaccuracies, email support@silverstay.com or text (301) 801-0025.';
const SCREEN_INTERNEL_PAGE_HEIGHT_CALCULATION = { xs: 'calc(100vh - 165px)', md: 'calc(100vh - 150px)' };
/** Same as `SCREEN_INTERNEL_PAGE_HEIGHT_CALCULATION`, minus extra chrome above the page card (e.g. a tab bar). */
const getScreenInternalPageHeight = (offsetPx = 0) => ({
    xs: `calc(100vh - ${165 + offsetPx}px)`,
    md: `calc(100vh - ${150 + offsetPx}px)`
});
export {
    SCREEN_INTERNEL_PAGE_HEIGHT_CALCULATION,
    getScreenInternalPageHeight,
    ET_TIMEZONE,
    JUST_SHOW_BARRIERS_WEEKDAYS_ET,
    ROWS_PER_PAGE_OPTIONS,
    DEFAULT_ROWS_PER_PAGE,
    TIMEZONE_OPTIONS,
    TIER_LEGEND,
    ENABLED_WEEKEND_DISCHARGE_HOSPITALS,
    READONLY_WEEKEND_DISCHARGE_HOSPITALS,
    WEEKEND_ONLY_HANDOFF_HOSPITALS,
    LLOS_FLAGGED_HOSPITALS,
    TRANSCRIPT_CRAWL_HOSPITALS,
    AI_EXTRACTED_HANDOFF_DETAILS_HOSPITALS,
    LLOS_SUMMARY_POINTS,
    TIER_COLORS,
    INTAKE_METHODS,
    SILVERSTAY_URL,
    SILVERSTAY_URL_WITHOUT_HTTP,
    SILVERSTAY_SUPPORT
};
export default LAYOUT_CONST;
